// リストアバーIDクリック検索と遷移後フォーカス制御
import { ref, nextTick } from 'vue';
import { findVideoItemById } from './useItemTypeGuards';

export const useRestoreIdSearch = ({
  items,
  detailSubTab,
  selectItem,
  fetchDetailStatus,
  storeFolderSelection,
  clearBasket,
  addToBasket,
  focusQuery = ref(''),
}) => {
  const clearKeywordOnNextFolderSelect = ref(false);

  const normalizeVideoId = (value) => {
    const raw = String(value ?? '').trim();
    if (raw === '' || !/^\d+$/.test(raw)) {
      return null;
    }

    const numeric = Number(raw);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      return null;
    }

    return String(Math.trunc(numeric));
  };

  const consumeFocusedVideoIdFromUrl = () => {
    try {
      const url = new URL(window.location.href);
      const focusedId = normalizeVideoId(url.searchParams.get('focus_id'));
      if (url.searchParams.has('focus_id')) {
        // サーバーが focus_id から 2 ページ目以降と求め、リダイレクトせずに描画した分は、
        // 変更前のリダイレクト先と同じクエリ (page 付き) をアドレスバーに使う
        if (focusQuery.value) {
          url.search = focusQuery.value;
        }
        url.searchParams.delete('focus_id');
        window.history.replaceState({}, '', url.toString());
      }
      return focusedId;
    } catch (error) {
      return null;
    }
  };

  const scrollToVideoElement = async (videoId) => {
    const normalizedId = normalizeVideoId(videoId);
    if (!normalizedId) {
      return;
    }

    await nextTick();
    const selector =
      `.dashboard-row[data-video-id="${normalizedId}"], ` +
      `.file-card[data-video-id="${normalizedId}"]`;
    const targetElement = document.querySelector(selector);
    if (!targetElement || typeof targetElement.scrollIntoView !== 'function') {
      return;
    }

    targetElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });
  };

  const applyFocusedVideoSelectionFromUrl = async () => {
    const focusedId = consumeFocusedVideoIdFromUrl();
    if (!focusedId) {
      return;
    }
    clearKeywordOnNextFolderSelect.value = true;

    const found = findVideoItemById(items.value, focusedId);
    if (!found) {
      return;
    }

    selectItem(found);
    fetchDetailStatus(found);
    clearBasket();
    addToBasket(found);
    detailSubTab.value = 'info';
    await scrollToVideoElement(focusedId);
  };

  const hasFocusIdInUrl = () => {
    try {
      const url = new URL(window.location.href);
      return url.searchParams.has('focus_id');
    } catch (error) {
      return false;
    }
  };

  const hasFolderIdInUrl = () => {
    try {
      const url = new URL(window.location.href);
      return url.searchParams.has('folder_id');
    } catch (error) {
      return false;
    }
  };

  const resolveVideoFolderId = async (videoId) => {
    const normalizedId = normalizeVideoId(videoId);
    if (!normalizedId) {
      return null;
    }

    try {
      const response = await window.axios.get(`/api/videos/${normalizedId}`);
      const payload = response?.data?.item ?? response?.data ?? null;
      const parsedFolderId = Number(payload?.folder_id);
      if (!Number.isFinite(parsedFolderId)) {
        return null;
      }

      const normalizedFolderId = Math.trunc(parsedFolderId);
      return normalizedFolderId > 0 ? String(normalizedFolderId) : null;
    } catch (error) {
      return null;
    }
  };

  const searchByRestoreId = async (videoId, scope = 'current') => {
    const normalizedId = normalizeVideoId(videoId);
    if (!normalizedId) {
      return;
    }

    const targetFolderId = await resolveVideoFolderId(normalizedId);
    if (targetFolderId) {
      storeFolderSelection(targetFolderId);
    }

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('q', normalizedId);
      url.searchParams.delete('page');
      url.searchParams.set('focus_id', normalizedId);

      if (targetFolderId) {
        url.searchParams.set('folder_id', targetFolderId);
      } else if (scope === 'global') {
        // 全体検索ではフォルダ絞り込みを明示的に解除する
        url.searchParams.set('folder_id', '');
      }

      window.location.href = url.toString();
    } catch (error) {
      const params = new URLSearchParams({
        q: normalizedId,
        focus_id: normalizedId,
      });
      if (targetFolderId) {
        params.set('folder_id', targetFolderId);
      } else if (scope === 'global') {
        params.set('folder_id', '');
      }
      window.location.href = `${window.location.pathname}?${params.toString()}`;
    }
  };

  return {
    clearKeywordOnNextFolderSelect,
    applyFocusedVideoSelectionFromUrl,
    hasFocusIdInUrl,
    hasFolderIdInUrl,
    searchByRestoreId,
  };
};
